import { CheckException, ExceptionDecisionStatus } from '@treasury/domain/arp';
import { TmBaseComponent } from '@treasury/presentation';
import { PropertyValueMap, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '../../components/jhd-decision-toggle';
// eslint-disable-next-line no-duplicate-imports
import { JhdDecisionToggleChangeEvent } from '../../components/jhd-decision-toggle';
import { JhaApprovalToggleStatus } from '../types';

/**
 * Pos Pay exceptions interface for jhd-decision-toggle.
 * Wraps jhd-decision-toggle component and provides an API to Pos Pay Domain Entity object for external state management.
 */
export const tagName = 'decision-toggle';
@customElement(tagName)
export class DecisionToggle extends TmBaseComponent {
    @property({ type: Object })
    public exception?: CheckException;

    @property()
    public decisionStatus?: ExceptionDecisionStatus;

    @property({ type: Boolean })
    public readonly = false;

    @property()
    public modified = false;

    @property()
    public decision = JhaApprovalToggleStatus.NA;

    public render() {
        if (!this.exception) {
            return nothing;
        }

        const status = this.decision && this.modified ? this.decision.toLowerCase() : 'na';
        return html`
            <jhd-decision-toggle
                class="flex items-center h-full border-l-4 status-${status}"
                .decision=${this.decision}
                @toggle-change=${(changeEvent: JhdDecisionToggleChangeEvent) => {
                    const { selection } = changeEvent.detail;
                    if (selection === JhaApprovalToggleStatus.Approved) {
                        this.payException();
                    } else if (selection === JhaApprovalToggleStatus.Rejected) {
                        this.returnException();
                    }
                    this.dispatchEvent(
                        new CustomEvent('change', {
                            detail: this.exception,
                            bubbles: true,
                            composed: true,
                        })
                    );
                }}
                .originalState=${this.exception.originalApprovalStatus}
                .modified=${this.modified}
                .filledBackground=${this.exception.decidedToday}
                .protected=${this.exception.protected}
                ?readonly=${this.readonly}
            ></jhd-decision-toggle>
        `;
    }

    protected updated(
        _changedProperties: PropertyValueMap<DecisionToggle> | Map<PropertyKey, unknown>
    ) {
        const relevantPropChanged =
            _changedProperties.has('exception') ||
            _changedProperties.has('decisionStatus') ||
            _changedProperties.has('modified');

        if (this.exception && relevantPropChanged) {
            const { decisionStatus, decisionModified } = this.exception;
            if (decisionStatus === ExceptionDecisionStatus.Pay) {
                this._payException();
            } else if (decisionStatus === ExceptionDecisionStatus.Return) {
                this._returnException();
            }
            this.modified = decisionModified;
        }
    }

    private payException() {
        if (!this.exception) {
            throw new Error('Could not pay exception; one was not defined.');
        }

        this.exception.pay();
        this.modified = this.exception.decisionModified;
        this._payException();
        this.dispatchEvent(new CustomEvent<CheckException>('change', { detail: this.exception }));
    }

    private _payException() {
        this.decision = JhaApprovalToggleStatus.Approved;
    }

    private returnException() {
        if (!this.exception) {
            throw new Error('Could not return exception; one was not defined.');
        }

        this.exception.return();
        this.modified = this.exception.decisionModified;
        this._returnException();
        this.dispatchEvent(new CustomEvent<CheckException>('change', { detail: this.exception }));
    }

    private _returnException() {
        this.decision = JhaApprovalToggleStatus.Rejected;
    }

    private acknowledgeException() {
        if (!this.exception) {
            throw new Error('Could not acknowledge exception; one was not defined.');
        }

        this.exception.acknowledged = !this.exception.acknowledged;
    }

    public static get styles() {
        return [
            css`
                .decision-icons {
                    display: flex;
                }
                .status-na {
                    border-color: transparent;
                }
                .status-approved {
                    border-color: var(--content-positive);
                }
                .status-rejected {
                    border-color: var(--content-negative);
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: DecisionToggle;
    }
}
